<template>
  <Popover>
    <PopoverTrigger as-child>
      <button class="filter-trigger">
        <ListFilterIcon class="h-3.5 w-3.5" />
        <span>Filter</span>
        <span v-if="activeFilterCount > 0" class="filter-count">{{ activeFilterCount }}</span>
      </button>
    </PopoverTrigger>

    <PopoverContent align="end" class="w-56 p-1.5" :side-offset="8">
      <div class="space-y-1">
        <div v-for="filter in filterConfigList" :key="filter.key" class="py-1">
          <div class="px-2 pb-1 text-[11px] font-medium uppercase tracking-[0.08em] text-gray-500">
            {{ filter.label }}
          </div>
          <button
            v-for="option in filterOptions(filter.key, filter.options)"
            :key="option"
            class="filter-row"
            :class="{ 'filter-row-active': filters[filter.key] === option }"
            @click="emit('filter-change', { key: filter.key, value: option })"
          >
            <span>{{ option }}</span>
            <CheckIcon v-if="filters[filter.key] === option" class="h-3.5 w-3.5" />
          </button>
        </div>

        <div v-if="activeFilterCount > 0" class="border-t border-gray-100 pt-1">
          <button class="filter-reset" @click="resetFilters">Reset filters</button>
        </div>
      </div>
    </PopoverContent>
  </Popover>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { CheckIcon, ListFilterIcon } from 'lucide-vue-next';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { defaultFilters, filterConfigList, type Filters } from './filterConfiguration';

const { filters } = defineProps<{ filters: Filters }>();

export type FilterChangePayload = {
  key: keyof Filters;
  value: string;
};

type Emits = {
  'filter-change': [payload: FilterChangePayload];
};

const emit = defineEmits<Emits>();

const filterOptions = (key: keyof Filters, options: readonly string[]) => {
  if (key === 'status') return ['Incomplete', 'Complete', 'All'];
  return ['All', ...options];
};

const activeFilterCount = computed(() => {
  let count = 0;
  if (filters.assignee !== defaultFilters.assignee) count += 1;
  if (filters.taskType !== defaultFilters.taskType) count += 1;
  if (filters.status !== defaultFilters.status) count += 1;
  return count;
});

const resetFilters = () => {
  emit('filter-change', { key: 'assignee', value: defaultFilters.assignee });
  emit('filter-change', { key: 'taskType', value: defaultFilters.taskType });
  emit('filter-change', { key: 'status', value: defaultFilters.status });
};
</script>

<style scoped>
@reference "tailwindcss";

.filter-trigger {
  @apply inline-flex items-center gap-1.5 rounded-md px-0 py-0 text-sm font-medium text-gray-500 transition-colors hover:text-gray-900;
}

.filter-count {
  @apply inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-gray-200 px-1 text-[10px] font-semibold text-gray-700;
}

.filter-row {
  @apply flex w-full items-center justify-between rounded-md px-2 py-1.5 text-sm text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900;
}

.filter-row-active {
  @apply bg-gray-100 font-medium text-gray-900;
}

.filter-reset {
  @apply w-full rounded-md px-2 py-1.5 text-left text-sm font-medium text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-900;
}
</style>
